import { useState } from "react";
import * as L from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { speak } from "@/lib/buddy";
import { BuddyAvatar } from "@/components/buddy/BuddyAvatar";
import { SceneOverlay } from "@/components/buddy/teach/SceneOverlay";

const ICONS = { brush: L.Brush, hat: L.HardHat, x: L.X, droplets: L.Droplets, shirt: L.Shirt, cookie: L.Cookie, smile: L.Smile, bed: L.Bed, candy: L.Candy,
  shield: L.Shield, hand: L.Hand, users: L.Users, bath: L.Bath, spray: L.SprayCan, footprints: L.Footprints, zap: L.Zap, calendar: L.Calendar,
  eye: L.Eye, wind: L.Wind, megaphone: L.Megaphone, lock: L.Lock, heart: L.Heart, trafficcone: L.TrafficCone, shieldcheck: L.ShieldCheck, phone: L.Phone, moon: L.Moon, tv: L.Tv, school: L.School, apple: L.Apple, clock: L.Clock, snowflake: L.Snowflake, sun: L.Sun };

export const CATEGORY_LABELS = { hygiene: "Self-care", routine: "When do I…?", feelings: "Feelings & kindness", safety: "Staying safe" };

export const CareScene = ({ scene, onResult, onNext }) => {
  const { buddy } = useProfile();
  const [res, setRes] = useState(null);
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);

  const choose = async (i) => {
    setBusy(true); setPicked(i);
    try {
      const r = await http.post("/buddy/teach/scene", { scene_id: scene.id, choice: i });
      setRes(r.data); onResult(r.data);
      speak(r.data.buddy_line, buddy.voice).catch(() => {});
      if (r.data.xp_gained) toast.success(`+${r.data.xp_gained} XP · ${buddy.name} learned something!`);
      r.data.new_badges.forEach((b) => toast.success(`New badge: ${b}`));
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const fixed = !!res?.correct;

  return (
    <div className="grid md:grid-cols-12 gap-8 items-center rise" data-testid={`care-scene-${scene.id}`}>
      <div className={`md:col-span-5 relative aspect-square rounded-[2rem] ${res && !res.correct ? "wiggle" : ""}`}>
        <BuddyAvatar buddy={buddy} className="w-full h-full !rounded-[2rem] text-7xl shadow-xl" testId="scene-avatar" />
        <SceneOverlay issue={scene.issue} fixed={fixed} />
      </div>
      <div className="md:col-span-7">
        <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C87D53]">{CATEGORY_LABELS[scene.category]}</span>
        <h3 className="font-display text-3xl text-[#2C4A3E] mt-2">{scene.title}</h3>
        <p className="text-lg text-stone-700 mt-3" data-testid="scene-problem">{scene.problem}</p>
        <button onClick={() => speak(scene.problem, buddy.voice).catch(() => {})} data-testid="scene-listen-btn" className="mt-2 text-sm text-[#6B9080] inline-flex items-center gap-1"><L.Volume2 className="w-4 h-4" /> Read it to me</button>
        <div className="grid gap-3 mt-5">
          {scene.options.map((o, i) => {
            const Icon = ICONS[o.icon] || L.Circle;
            const state = picked === i && res ? (res.correct ? "bg-[#C8DCCB] border-[#6B9080]" : "bg-[#F6D9D3] border-[#E8B4B8]") : "bg-white/80 border-[#E6DEC3] hover:border-[#2C4A3E]";
            return (
              <button key={i} disabled={busy || fixed} onClick={() => choose(i)} data-testid={`scene-option-${i}`}
                className={`flex items-center gap-4 text-left px-5 py-4 rounded-2xl border-2 text-base transition-colors disabled:cursor-default ${state}`}>
                <span className="w-11 h-11 rounded-xl bg-[#F7F3EB] grid place-items-center shrink-0"><Icon className="w-5 h-5 text-[#2C4A3E]" /></span>{o.label}
              </button>
            );
          })}
        </div>
        {res && (
          <div className={`mt-5 rounded-2xl p-4 ${res.correct ? "bg-[#E3EBE5]" : "bg-[#FBEDE8]"}`} data-testid="scene-buddy-line">
            <p className="font-display text-[#2C4A3E]"><b>{buddy.name}:</b> {res.buddy_line}</p>
            {!res.correct && <button onClick={() => { setRes(null); setPicked(null); }} data-testid="scene-try-again-btn" className="mt-2 text-sm underline text-[#C87D53]">Try another way</button>}
          </div>
        )}
        {fixed && <button onClick={onNext} data-testid="scene-next-btn" className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#2C4A3E] text-white hover:bg-[#1E352B]">Next <L.ArrowRight className="w-4 h-4" /></button>}
      </div>
    </div>
  );
};
